import { computed, ref, watchEffect, type Ref, type ComputedRef } from 'vue';

import BigNumber from '@exchange/helpers/bignumber';
import type { MarketModel } from '@exchange/libs/market/service/src';
import { orderformService, getSlippageMultiplier, getPriceWithSlippage } from '@exchange/libs/order/order-form/service/src';
import { OrderSide } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { Orderbook } from '@exchange/libs/orderbook/service/src/lib/orderbook-model/index';
import orderbookService from '@exchange/libs/orderbook/service/src/lib/orderbook.service';
import { settingsService } from '@exchange/libs/settings/service/src';

const useOrderFormPrice = (market: Ref<MarketModel>, isMarketOrder: ComputedRef<boolean>) => {
  const marketOrderPrice = ref();
  const marketOrderMinAmount = ref('0');

  const isBuyOrder = computed(() => orderformService.data.side === OrderSide.BUY);
  const marketOrdersSlippage = computed(() => settingsService.settings.userSettings.marketOrdersSlippage);

  const currentMinPrice = computed(() => {
    const { marketPrecision, minPrice } = market.value;

    return minPrice.toFixed(marketPrecision);
  });
  const currentMaxPrice = computed(() => {
    const { marketPrecision, maxPrice } = market.value;

    return maxPrice.toFixed(marketPrecision);
  });

  const getOrderBook = async () => {
    const { id, marketPrecision } = market.value;
    const orderbook = await orderbookService.getOrderbook({ id, precision: marketPrecision }).catch(() => undefined);

    return orderbook;
  };

  const getBasePrice = (orderbook: Orderbook, isBuySide: boolean, lastPrice: number) => {
    const bestAsk = orderbook.bestAsk?.price;
    const bestBid = orderbook.bestBid?.price;

    return (isBuySide ? bestAsk : bestBid) ?? lastPrice ?? 0;
  };

  watchEffect(async () => {
    const { lastPrice, minSize, amountPrecision, totalPrecision } = market.value;
    const isBuy = isBuyOrder.value;
    const maxPrice = currentMaxPrice.value;
    const minPrice = currentMinPrice.value;
    const slippagePercent = marketOrdersSlippage.value;

    if (!isMarketOrder.value) {
      return;
    }

    const currentOrderBook = await getOrderBook();

    if (!currentOrderBook) {
      return;
    }

    const basePrice = getBasePrice(currentOrderBook.value, isBuy, lastPrice);
    const slippageMultiplier = getSlippageMultiplier(slippagePercent, isBuy);

    const price = getPriceWithSlippage({
      minPrice,
      maxPrice,
      basePrice,
      slippageMultiplier,
      precision: totalPrecision,
    });

    marketOrderPrice.value = price;
    const minAmount = Number.isNaN(price) ? new BigNumber(0) : new BigNumber(minSize).div(price);

    marketOrderMinAmount.value = minAmount.toFixed(amountPrecision, 0);
  });

  return {
    marketOrderPrice,
    marketOrderMinAmount,
    currentMinPrice,
    currentMaxPrice,
  };
};

export default useOrderFormPrice;
